import * as fs from "node:fs";
import * as path from "node:path";
import { execFileSync } from "node:child_process";
import { LocalCliError } from "./errors.js";

/**
 * Shared mechanics for "download a repo tarball into a per-content cache".
 *
 * Used by release-content.ts (platform release content) and agent-content.ts
 * (agent-repo content). The extraction is trivial; the atomic publish is not —
 * it has to tolerate a concurrent writer that already published the same content
 * while still surfacing every other failure, and getting that subtly wrong in two
 * places is how a poisoned partial cache ships.
 */

/**
 * Extract a .tar.gz, stripping the single leading component GitHub's tarballs
 * wrap everything in (`<owner>-<repo>-<sha>/`).
 *
 * Uses the system tar (present on macOS/Linux; Git-for-Windows ships bsdtar).
 * tar's -z handles gzip directly — no need to gunzip in JS first.
 */
export function extractTarGz(tgz: string, into: string): void {
  execFileSync("tar", ["-xzf", tgz, "-C", into, "--strip-components=1"]);
}

/**
 * Rename `from` into `dest`, treating only a race against a writer that already
 * published this exact content as benign.
 *
 * `sentinel` is a path relative to `dest` whose existence means "a complete
 * publish is already there". Any failure that is NOT that race must surface —
 * silently accepting one would leave callers reading a directory that does not
 * contain what they asked for.
 */
export function publishAtomically(args: {
  from: string;
  dest: string;
  sentinel: string;
  errorCode: string;
}): void {
  try {
    fs.renameSync(args.from, args.dest);
  } catch (e) {
    const err = e as NodeJS.ErrnoException;
    const isBenignRace =
      (err.code === "EEXIST" || err.code === "ENOTEMPTY") &&
      fs.existsSync(path.join(args.dest, args.sentinel));
    if (!isBenignRace) {
      throw new LocalCliError({
        code: args.errorCode,
        message: `Could not publish cache to ${args.dest}: ${err.message}`,
      });
    }
  }
}
